import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { products, categories, articles } from '@/mocks/baoyu';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug = 'all' } = useParams();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(slug);

  useEffect(() => {
    setActiveCategory(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const filteredProducts =
    activeCategory === 'all'
      ? products
      : products.filter((p) => p.category === activeCategory);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryTitle = currentCategory ? currentCategory.name : '全部产品';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - baoyu`,
    description: `浏览 baoyu ${categoryTitle}系列产品，了解更多关于baoyu产品的详细信息、功能特色和用户评价`,
    url: `https://bayo1u.github.io/category/${slug}`,
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryTitle },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="mb-8">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-8">
            {categoryTitle}
          </h1>

          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Products grid */}
          <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/category/${product.category}`}
                className="group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-background-300/60 cursor-pointer"
              >
                <div className="relative w-full aspect-[4/3] overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    title={`${product.name} - baoyu产品`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-1.5">
                    {product.name}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {product.features.map((f) => (
                      <span
                        key={f}
                        className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${
                            i < Math.floor(product.rating)
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-foreground-400'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs text-foreground-600">{product.rating}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Related articles */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-background-300/60 cursor-pointer"
                >
                  <div className="relative w-full aspect-[16/9] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-primary-500 font-medium">{article.category === 'news' ? '新闻' : article.category === 'review' ? '评测' : article.category === 'guide' ? '指南' : '故事'}</span>
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}